import { useCallback, useEffect, useRef } from 'react';
import type {
  AnalyticsAllStats,
  AnalyticsDialogsRequest,
  GetAllStatsAnalyticsStatsAllPostApiArg,
} from '@shared/api';
import { useGetAllStatsAnalyticsStatsAllPostMutation } from '@shared/api';

type AnalyticsAllStatsResult = {
  data?: AnalyticsAllStats;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => void;
  reset: () => void;
};

export const useAllStats = (
  params: AnalyticsDialogsRequest | null,
  enabled = true,
): AnalyticsAllStatsResult => {
  const [trigger, result] = useGetAllStatsAnalyticsStatsAllPostMutation();
  const lastArgsRef = useRef<GetAllStatsAnalyticsStatsAllPostApiArg | null>(null);

  const fetch = useCallback(
    async (p: AnalyticsDialogsRequest) => {
      const arg: GetAllStatsAnalyticsStatsAllPostApiArg = {
        analyticsDialogsRequest: p,
      };
      lastArgsRef.current = arg;
      try {
        await trigger(arg).unwrap();
      } catch {}
    },
    [trigger],
  );

  const refetch = useCallback(() => {
    if (!lastArgsRef.current) return;
    void trigger(lastArgsRef.current)
      .unwrap()
      .catch(() => {});
  }, [trigger]);

  useEffect(() => {
    if (!enabled || !params) return;
    void fetch(params);
  }, [enabled, params, fetch]);

  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetch,
    reset: result.reset,
  };
};
